import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { getEstadoCuenta, getAvisos } from "../services/portalService";
import { useEsCliente } from "../../../shared/hooks/useTenantTipo";
import InstalarCorvana from "../../../shared/components/InstalarCorvana";
import EstadoCuentaModal from "../components/EstadoCuentaModal";

function fmt(val) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "CRC", minimumFractionDigits: 2,
  }).format(Math.abs(parseFloat(val || 0)));
}

function SkeletonLine({ w = "100%", h = 16, radius = 6 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: "var(--hover-bg)", animation: "pdSkel 1.4s ease infinite",
    }} />
  );
}

function useGreeting(t) {
  const h = new Date().getHours();
  if (h >= 5 && h < 12)  return t("portal.buenosDias");
  if (h >= 12 && h < 19) return t("portal.buenasTardes");
  return t("portal.buenasNoches");
}

export default function PortalDashboard() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const greeting = useGreeting(t);
  const esCliente = useEsCliente();

  const [data,    setData]    = useState(null);
  const [avisos,  setAvisos]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [verEstado, setVerEstado] = useState(false);

  const firstName    = localStorage.getItem("first_name") || "";
  const filialNombre = localStorage.getItem("filial_nombre") || "";
  const condoNombre  = localStorage.getItem("condominio_nombre") || "";

  useEffect(() => {
    Promise.all([
      getEstadoCuenta().then(setData).catch(() => {}),
      getAvisos({ page_size: 3 }).then(d => setAvisos(d.results || d)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const saldo    = parseFloat(data?.saldo || 0);
  const alDia    = saldo <= 0;
  const ultimoCargo = data?.movimientos?.find(m => m.tipo === "cargo") || null;

  // ── Palette ──────────────────────────────────────────────────────────────────
  const card     = isLight ? "var(--bg-card)" : "#1e2433";
  const cardBdr  = isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)";
  const txtPri   = isLight ? "#0f172a" : "#f1f5f9";
  const txtSec   = isLight ? "#64748b" : "#94a3b8";
  const shadow   = isLight ? "0 2px 12px rgba(0,0,0,.08)" : "0 4px 20px rgba(0,0,0,.35)";
  // Verde, rojo y celeste pastel no se leen sobre el fondo claro: en el tema
  // claro van en su tono oscuro.
  const verde    = isLight ? "#15803d" : "#4ade80";
  const rojo     = isLight ? "#b91c1c" : "#f87171";
  const celeste  = isLight ? "#0369a1" : "#38bdf8";

  // Hero colors — fully adaptive between light and dark
  const heroBg = isLight
    ? "linear-gradient(135deg, #fff7ed 0%, #ffedd5 45%, #fdf4ff 100%)"
    : "linear-gradient(135deg, #2d1f3d 0%, #1e1040 50%, #0f1a35 100%)";
  const heroSub    = isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.5)";
  const heroBorder = isLight ? "rgba(249,115,22,0.25)" : "rgba(249,115,22,0.18)";
  const heroShadow = isLight
    ? "0 4px 24px rgba(249,115,22,0.14), 0 1px 4px rgba(0,0,0,.06)"
    : "0 0 0 1px rgba(249,115,22,.18), 0 8px 48px rgba(139,92,246,.25), 0 2px 8px rgba(0,0,0,.5)";

  const quickLinks = [
    {
      to: "/portal/reservaciones",
      icon: "fa-calendar-check",
      label: t("portal.reservaciones"),
      desc: t("portal.reservacionesDesc"),
      color: isLight ? "#4f46e5" : "#818cf8",
    },
    {
      to: "/portal/avisos-pago",
      icon: "fa-money-bill-transfer",
      label: t("portal.avisosPago"),
      desc: t("portal.avisosPagoDesc"),
      color: "#f97316",
    },
    {
      to: "/portal/avisos",
      icon: "fa-bell",
      label: t("portal.avisos"),
      desc: t("portal.avisosDesc"),
      color: celeste,
    },
    ...(esCliente ? [] : [{
      to: "/portal/autorizados",
      icon: "fa-user-shield",
      label: t("portal.misAutorizados"),
      desc: t("portal.misAutorizadosDesc"),
      color: isLight ? "#047857" : "#34d399",
    }]),
  ];

  return (
    <>
      <style>{`
        @keyframes pdSkel { 0%,100%{opacity:.4} 50%{opacity:.8} }
        @keyframes pdFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }

        .pd-card { transition: box-shadow .18s, transform .18s; }
        .pd-card:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,.22) !important; }

        .pd-quick { transition: background .15s, transform .18s, box-shadow .18s; }
        .pd-quick:hover { transform: translateY(-3px) !important; }

        .pd-mov-row { transition: background .12s; }
        .pd-mov-row:hover { background: var(--hover-bg) !important; }

        /* minmax(0,1fr) en vez de 1fr: con 1fr la pista nunca baja del
           min-content del contenido, y como la descripción de cada tarjeta va
           en nowrap, las columnas se inflaban y la tercera tarjeta ("Avisos
           del Condominio") se salía del contenedor y quedaba cortada. */
        /* Hero layout */
        .pd-top { display:grid; grid-template-columns:minmax(0,1fr) 300px; gap:20px; margin-bottom:20px; }
        /* Quick links row */
        .pd-quick-row { display:grid; grid-template-columns:repeat(var(--pd-cols,3),minmax(0,1fr)); gap:16px; margin-bottom:20px; }
        /* Bottom two-col */
        .pd-bottom { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.4fr); gap:20px; margin-bottom:20px; }
        .pd-quick-row > * { min-width:0; }

        @media(max-width:1024px) {
          .pd-top { grid-template-columns:minmax(0,1fr); }
          .pd-bottom { grid-template-columns:minmax(0,1fr); }
        }
        @media(max-width:1280px) {
          .pd-quick-row { grid-template-columns:repeat(2,minmax(0,1fr)); }
        }
        @media(max-width:520px) {
          .pd-quick-row { grid-template-columns:minmax(0,1fr); }
        }
      `}</style>

      <div style={{ padding: "28px 28px 48px", animation: "pdFadeUp .35s ease both" }}>

        {/* ── Greeting ── */}
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ margin: 0, fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 800, color: txtPri, lineHeight: 1.2 }}>
            {greeting}{firstName ? `, ${firstName}` : ""} 👋
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: txtSec }}>
            <span style={{ color: "#f97316", fontWeight: 700 }}>{filialNombre}</span>
            {condoNombre && (
              <> &nbsp;·&nbsp; {t("portal.enCondominio")} <span style={{ fontWeight: 600 }}>{condoNombre}</span></>
            )}
          </p>
        </div>

        {/* ── Top row: Hero + Estado de cuenta ── */}
        <div className="pd-top">

          {/* Hero — balance + CTA */}
          <div style={{
            background: heroBg,
            borderRadius: 20, padding: "32px 36px",
            position: "relative", overflow: "hidden",
            border: `1px solid ${heroBorder}`,
            boxShadow: heroShadow,
            minHeight: 200,
          }}>
            {/* Decorative orbs */}
            <div style={{ position:"absolute", top:-70, right:-70, width:260, height:260, borderRadius:"50%",
              background: isLight
                ? "radial-gradient(circle, rgba(249,115,22,.30) 0%, transparent 65%)"
                : "radial-gradient(circle, rgba(249,115,22,.38) 0%, transparent 65%)",
              pointerEvents:"none" }} />
            <div style={{ position:"absolute", bottom:-60, left:40, width:200, height:200, borderRadius:"50%",
              background: isLight
                ? "radial-gradient(circle, rgba(168,85,247,.18) 0%, transparent 65%)"
                : "radial-gradient(circle, rgba(139,92,246,.35) 0%, transparent 65%)",
              pointerEvents:"none" }} />

            <div style={{ position:"relative" }}>
              <p style={{ margin:"0 0 6px", fontSize:12, fontWeight:700, letterSpacing:1.5,
                textTransform:"uppercase", color: heroSub }}>
                {t("portal.tuSaldo")}
              </p>

              {loading ? (
                <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                  <SkeletonLine w={180} h={42} radius={8} />
                  <SkeletonLine w={120} h={14} />
                </div>
              ) : (
                <>
                  <div style={{ fontSize:"clamp(32px,4vw,48px)", fontWeight:900,
                    color: alDia
                      ? (isLight ? "#15803d" : "#4ade80")
                      : (isLight ? "#dc2626" : "#f87171"),
                    fontFamily:"Inter,sans-serif", lineHeight:1, marginBottom:6 }}>
                    {fmt(Math.abs(saldo))}
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:24,
                    color: alDia
                      ? (isLight ? "#16a34a" : "rgba(74,222,128,.8)")
                      : (isLight ? "#ef4444" : "rgba(248,113,113,.8)") }}>
                    {alDia ? t("portal.sinPendientes") : t("portal.saldoPendiente")}
                  </div>
                </>
              )}

              {!alDia && (
                <Link to="/portal/avisos-pago" style={{ textDecoration:"none" }}>
                  <button style={{
                    background: "linear-gradient(135deg,#f97316,#ea580c)",
                    color:"#fff", border:"none", borderRadius:12,
                    padding:"11px 22px", fontSize:14, fontWeight:700,
                    cursor:"pointer", display:"inline-flex", alignItems:"center", gap:8,
                    boxShadow:"0 4px 18px rgba(249,115,22,.45)",
                    transition:"transform .15s, box-shadow .15s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 6px 24px rgba(249,115,22,.55)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 4px 18px rgba(249,115,22,.45)"; }}>
                    <i className="fa-solid fa-paper-plane" />
                    {t("portal.pagarAhora")}
                  </button>
                </Link>
              )}
              {alDia && (
                <div style={{ display:"inline-flex", alignItems:"center", gap:8,
                  background: isLight ? "rgba(22,163,74,.10)" : "rgba(74,222,128,.12)",
                  border: `1px solid ${isLight ? "rgba(22,163,74,.35)" : "rgba(74,222,128,.3)"}`,
                  borderRadius:12, padding:"10px 18px" }}>
                  <i className="fa-solid fa-circle-check" style={{ color: isLight ? "#16a34a" : "#4ade80" }} />
                  <span style={{ color: isLight ? "#16a34a" : "#4ade80", fontWeight:700, fontSize:14 }}>{t("portal.alDia")}</span>
                </div>
              )}
            </div>
          </div>

          {/* Estado de cuenta card */}
          <div style={{
            background: card, border:`1px solid ${cardBdr}`,
            borderRadius:20, padding:"28px 24px",
            boxShadow: shadow, display:"flex", flexDirection:"column", gap:20,
          }}>
            <div>
              <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:700, letterSpacing:1.4,
                textTransform:"uppercase", color:txtSec }}>{t("portal.estadoCuenta")}</p>
              {loading ? <SkeletonLine w="60%" h={28} /> : (
                <div style={{ fontSize:22, fontWeight:800,
                  color: alDia ? verde : rojo }}>
                  {alDia ? t("portal.alDia") : t("portal.adeuda")}
                </div>
              )}
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {[
                { label: t("portal.totalCargos"), val: data?.total_cargos, color: rojo, icon:"fa-arrow-up" },
                { label: t("portal.totalAbonos"), val: data?.total_abonos,  color: verde, icon:"fa-arrow-down" },
              ].map(({ label, val, color, icon }) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:`${color}18`,
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <i className={`fa-solid ${icon}`} style={{ color, fontSize:11 }} />
                    </div>
                    <span style={{ fontSize:13, color:txtSec, fontWeight:500 }}>{label}</span>
                  </div>
                  {loading ? <SkeletonLine w={80} h={14} /> : (
                    <span style={{ fontSize:14, fontWeight:700, color, fontFamily:"Inter,sans-serif" }}>
                      {fmt(val || 0)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div style={{ borderTop:`1px solid ${cardBdr}`, paddingTop:14 }}>
              <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:700, letterSpacing:1.2,
                textTransform:"uppercase", color:txtSec }}>{t("portal.unidadResidencia")}</p>
              <p style={{ margin:0, fontSize:15, fontWeight:700, color: txtPri }}>{filialNombre}</p>
              {condoNombre && (
                <p style={{ margin:"2px 0 0", fontSize:12, color:txtSec }}>{condoNombre}</p>
              )}
            </div>

            <button type="button" onClick={() => setVerEstado(true)} style={{
              marginTop:"auto", width:"100%", minHeight:42, borderRadius:10, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              background:"rgba(var(--brand-rgb), 0.12)", border:"1px solid rgba(var(--brand-rgb), 0.4)",
              color:"var(--brand)", fontWeight:700, fontSize:13,
            }}>
              <i className="fa-solid fa-file-arrow-down" style={{ color:"inherit" }} />
              {t("portal.descargarEstado")}
            </button>
          </div>
        </div>
        {verEstado && <EstadoCuentaModal onClose={() => setVerEstado(false)} />}

        {/* ── Quick actions row ── */}
        <div className="pd-quick-row" style={{ "--pd-cols": quickLinks.length }}>
          {quickLinks.map(({ to, icon, label, desc, color }) => (
            <Link key={to} to={to} style={{ textDecoration:"none" }}>
              <div className="pd-quick" style={{
                background: card,
                border:`1px solid ${color}28`,
                borderRadius:16, padding:"20px 20px",
                display:"flex", alignItems:"center", gap:14,
                boxShadow: shadow,
                cursor:"pointer",
              }}>
                <div style={{
                  width:48, height:48, borderRadius:14, flexShrink:0,
                  background:`${color}18`, border:`1px solid ${color}30`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <i className={`fa-solid ${icon}`} style={{ color, fontSize:20 }} />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:txtPri, marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:12, color:txtSec, overflow:"hidden",
                    textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{desc}</div>
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color:`${color}66`, fontSize:12, flexShrink:0 }} />
              </div>
            </Link>
          ))}
        </div>

        {/* ── Instalar la app en el celular (se oculta si ya está instalada) ── */}
        <InstalarCorvana modo="tarjeta" condominio={condoNombre} />

        {/* ── Bottom row: Próximo cargo + Actividad reciente ── */}
        <div className="pd-bottom">

          {/* Próximo / último cargo */}
          <div style={{
            background: card, border:`1px solid ${cardBdr}`,
            borderRadius:16, padding:"24px 22px", boxShadow:shadow,
          }}>
            <p style={{ margin:"0 0 16px", fontSize:11, fontWeight:700, letterSpacing:1.4,
              textTransform:"uppercase", color:txtSec }}>{t("portal.proximoPago")}</p>

            {loading ? (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <SkeletonLine w="80%" h={18} />
                <SkeletonLine w="55%" h={14} />
                <SkeletonLine w="40%" h={28} radius={8} />
              </div>
            ) : !ultimoCargo ? (
              <div style={{ textAlign:"center", padding:"24px 0", color:txtSec }}>
                <i className="fa-solid fa-check-circle" style={{ fontSize:30, color: verde, display:"block", marginBottom:8 }} />
                <p style={{ margin:0, fontSize:13 }}>{t("portal.sinPendientes")}</p>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <div style={{ width:40, height:40, borderRadius:11,
                    background:"rgba(248,113,113,.12)", border:"1px solid rgba(248,113,113,.25)",
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <i className="fa-solid fa-file-invoice" style={{ color: rojo, fontSize:16 }} />
                  </div>
                  <div>
                    <p style={{ margin:0, fontSize:13, fontWeight:600, color:txtPri }}>{ultimoCargo.detalle}</p>
                    <p style={{ margin:"2px 0 0", fontSize:11, color:txtSec }}>{ultimoCargo.fecha}</p>
                  </div>
                </div>
                <div style={{ fontSize:28, fontWeight:900, color: rojo, fontFamily:"Inter,sans-serif" }}>
                  {fmt(ultimoCargo.monto)}
                </div>
                {!alDia && (
                  <Link to="/portal/avisos-pago" style={{ textDecoration:"none" }}>
                    <button style={{
                      marginTop:16, width:"100%",
                      background:"linear-gradient(135deg,#f97316,#ea580c)",
                      color:"#fff", border:"none", borderRadius:10,
                      padding:"10px 16px", fontSize:13, fontWeight:700,
                      cursor:"pointer", display:"flex", alignItems:"center",
                      justifyContent:"center", gap:8,
                    }}>
                      <i className="fa-solid fa-paper-plane" />
                      {t("portal.pagarAhora")}
                    </button>
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Actividad reciente */}
          <div style={{
            background: card, border:`1px solid ${cardBdr}`,
            borderRadius:16, overflow:"hidden", boxShadow:shadow,
          }}>
            <div style={{ padding:"18px 20px 12px", display:"flex",
              justifyContent:"space-between", alignItems:"center",
              borderBottom:`1px solid ${cardBdr}` }}>
              <p style={{ margin:0, fontSize:11, fontWeight:700, letterSpacing:1.4,
                textTransform:"uppercase", color:txtSec }}>{t("portal.actividadReciente")}</p>
            </div>

            {loading ? (
              <div style={{ padding:18, display:"flex", flexDirection:"column", gap:14 }}>
                {[...Array(4)].map((_, i) => (
                  <div key={i} style={{ display:"flex", gap:12, alignItems:"center" }}>
                    <SkeletonLine w={36} h={36} radius={9} />
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                      <SkeletonLine w="65%" h={12} />
                      <SkeletonLine w="35%" h={10} />
                    </div>
                    <SkeletonLine w={80} h={14} />
                  </div>
                ))}
              </div>
            ) : !data?.movimientos?.length ? (
              <div style={{ padding:"40px 20px", textAlign:"center", color:txtSec }}>
                <i className="fa-solid fa-inbox" style={{ fontSize:32, opacity:0.3, display:"block", marginBottom:10 }} />
                <p style={{ margin:0, fontSize:13 }}>{t("portal.sinMovimientos")}</p>
              </div>
            ) : (
              data.movimientos.slice(0, 6).map((m, i, arr) => {
                const isCargo = m.tipo === "cargo";
                return (
                  <div key={i} className="pd-mov-row" style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"12px 20px",
                    borderBottom: i < Math.min(arr.length, 6) - 1 ? `1px solid ${cardBdr}` : "none",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{
                        width:34, height:34, borderRadius:9, flexShrink:0,
                        background: isCargo ? "rgba(248,113,113,.12)" : "rgba(74,222,128,.12)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <i className={`fa-solid ${isCargo ? "fa-file-invoice" : "fa-circle-check"}`}
                          style={{ color: isCargo ? rojo : verde, fontSize:13 }} />
                      </div>
                      <div>
                        <p style={{ margin:0, fontSize:12, fontWeight:600, color:txtPri }}>{m.detalle}</p>
                        <p style={{ margin:"1px 0 0", fontSize:11, color:txtSec }}>{m.fecha}</p>
                      </div>
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, fontFamily:"Inter,sans-serif",
                      color: isCargo ? rojo : verde, whiteSpace:"nowrap" }}>
                      {isCargo ? "+" : "−"}{fmt(m.monto)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Avisos importantes ── */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <p style={{ margin:0, fontSize:11, fontWeight:700, letterSpacing:1.4,
              textTransform:"uppercase", color:txtSec }}>{t("portal.avisosImportantes")}</p>
            <Link to="/portal/avisos" style={{ textDecoration:"none", fontSize:12,
              color:"#f97316", fontWeight:700 }}>{t("portal.verTodos")}</Link>
          </div>

          {loading ? (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {[...Array(2)].map((_, i) => (
                <div key={i} style={{ background:card, borderRadius:14,
                  border:`1px solid ${cardBdr}`, padding:"18px 20px",
                  display:"flex", flexDirection:"column", gap:8 }}>
                  <SkeletonLine w="55%" h={14} />
                  <SkeletonLine w="80%" h={11} />
                </div>
              ))}
            </div>
          ) : avisos.length === 0 ? (
            <div style={{
              background:card, border:`1px solid ${cardBdr}`,
              borderRadius:14, padding:"36px 20px", textAlign:"center", color:txtSec,
            }}>
              <i className="fa-solid fa-bell-slash" style={{ fontSize:28, opacity:0.3, display:"block", marginBottom:8 }} />
              <p style={{ margin:0, fontSize:13 }}>{t("portal.sinAvisosImportantes")}</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {avisos.map(a => (
                <div key={a.id} className="pd-card" style={{
                  background:card, border:`1px solid ${cardBdr}`,
                  borderRadius:14, padding:"18px 20px",
                  boxShadow:shadow, display:"flex", gap:14, alignItems:"flex-start",
                }}>
                  <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
                    background:"rgba(56,189,248,.14)", border:"1px solid rgba(56,189,248,.25)",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <i className="fa-solid fa-bullhorn" style={{ color: celeste, fontSize:15 }} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ margin:"0 0 3px", fontSize:14, fontWeight:700,
                      color:txtPri, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {a.titulo}
                    </p>
                    {a.cuerpo && (
                      <p style={{ margin:"0 0 4px", fontSize:12, color:txtSec, lineHeight:1.5,
                        display:"-webkit-box", WebkitLineClamp:2,
                        WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                        {a.cuerpo}
                      </p>
                    )}
                    <p style={{ margin:0, fontSize:11, color:txtSec }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                  {/* Con varios adjuntos no hay un único enlace que mostrar:
                      se marca la cantidad y el detalle del aviso los lista. */}
                  {(a.archivos || []).length > 0 && (
                    <Link to="/portal/avisos"
                      style={{ color: celeste, fontSize:12, fontWeight:600,
                        textDecoration:"none", whiteSpace:"nowrap", flexShrink:0 }}>
                      <i className="fa-solid fa-paperclip" style={{ marginRight:4 }} />
                      {a.archivos.length === 1
                        ? t("portal.verArchivo")
                        : `${a.archivos.length} ${t("portal.tieneArchivos")}`}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
